import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import Login from './pages/Login'
import FieldLayout from './layouts/FieldLayout'
import UpdateBanner from './components/UpdateBanner'
import FailedQueueBanner from './components/FailedQueueBanner'

// Code-splitting: every page below ships as its own chunk. Sales staff
// loading Field Mode no longer pay the cost of fetching Office Mode
// admin code, and admin opening Dashboard doesn't drag NewSale into the
// initial bundle. The initial bundle is now login + layouts + auth +
// PWA scaffolding only; everything else loads on first navigation.
const FieldHome = lazy(() => import('./pages/field/Home'))
const NewSale = lazy(() => import('./pages/field/NewSale'))
const NewPurchase = lazy(() => import('./pages/field/NewPurchase'))
const VanInventory = lazy(() => import('./pages/field/VanInventory'))
const CollectPayment = lazy(() => import('./pages/field/CollectPayment'))
const SalesReturn = lazy(() => import('./pages/field/SalesReturn'))
const FieldHistory = lazy(() => import('./pages/field/FieldHistory'))
const NewQuotation = lazy(() => import('./pages/field/NewQuotation'))
const DueToday = lazy(() => import('./pages/field/DueToday'))
const OverdueInvoices = lazy(() => import('./pages/field/OverdueInvoices'))
const FieldProducts = lazy(() => import('./pages/field/FieldProducts'))
const FieldCustomers = lazy(() => import('./pages/field/FieldCustomers'))
const FieldCustomerStatement = lazy(() => import('./pages/field/FieldCustomerStatement'))
const FieldPriceCheck = lazy(() => import('./pages/field/FieldPriceCheck'))
const FieldExpenses = lazy(() => import('./pages/field/FieldExpenses'))
const FieldAccounts = lazy(() => import('./pages/field/FieldAccounts'))
const FieldManage = lazy(() => import('./pages/field/FieldManage'))
const FieldReports = lazy(() => import('./pages/field/FieldReports'))
const FieldSuppliers = lazy(() => import('./pages/field/FieldSuppliers'))
const FieldSupplierStatement = lazy(() => import('./pages/field/FieldSupplierStatement'))
const FieldWarehouse = lazy(() => import('./pages/field/FieldWarehouse'))
const FieldUsers = lazy(() => import('./pages/field/FieldUsers'))
const FieldSettings = lazy(() => import('./pages/field/FieldSettings'))
const FieldDocSetup = lazy(() => import('./pages/field/FieldDocSetup'))
const PaySupplier = lazy(() => import('./pages/field/PaySupplier'))

// Owner mode — cross-tenant, read-only. Outside FieldLayout on purpose: it
// is not one more field screen but a different mode, and it has no bottom
// nav because there is nothing here to do, only to see.
const OwnerMonitor = lazy(() => import('./pages/owner/Monitor'))
const OwnerOperator = lazy(() => import('./pages/owner/OperatorDetail'))
const OwnerAddVan = lazy(() => import('./pages/owner/AddVan'))
const OwnerAddOperator = lazy(() => import('./pages/owner/AddOperator'))

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="spinner w-8 h-8" />
  </div>
)

/**
 * A manager-only screen.
 *
 * Sends a driver home rather than showing an error: these URLs are reached
 * by a stale bookmark or a shared link, not by choice, and a dead end is
 * more alarming than simply arriving at Home. The API refuses the data
 * regardless of what renders.
 */
function ManagerRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) return <RouteFallback />
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'admin') return <Navigate to="/field" replace />
  return <>{children}</>
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="screen-min flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export default function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <UpdateBanner />
      <FailedQueueBanner />
      <Routes>
        <Route path="/login" element={<Login />} />

        {/* Field Mode (Sales Staff) */}
        <Route
          path="/field"
          element={
            <ProtectedRoute>
              <FieldLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<FieldHome />} />
          <Route path="sale" element={<NewSale />} />
          <Route path="purchase" element={<NewPurchase />} />
          <Route path="inventory" element={<VanInventory />} />
          <Route path="collect" element={<CollectPayment />} />
          <Route path="return" element={<SalesReturn />} />
          <Route path="quotation" element={<NewQuotation />} />
          <Route path="due-today" element={<DueToday />} />
          <Route path="overdue" element={<OverdueInvoices />} />
          <Route path="products" element={<FieldProducts />} />
          <Route path="customers" element={<FieldCustomers />} />
          <Route path="customers/:customerId" element={<FieldCustomerStatement />} />
          <Route path="history" element={<FieldHistory />} />
          <Route path="expenses" element={<FieldExpenses />} />
          <Route path="accounts" element={<FieldAccounts />} />
          <Route path="manage" element={<ManagerRoute><FieldManage /></ManagerRoute>} />
          <Route path="reports" element={<ManagerRoute><FieldReports /></ManagerRoute>} />
          <Route path="suppliers" element={<ManagerRoute><FieldSuppliers /></ManagerRoute>} />
          <Route path="suppliers/:supplierId" element={<ManagerRoute><FieldSupplierStatement /></ManagerRoute>} />
          {/* Was open to drivers, back when a copy held one self-employed
              operator who logged and settled his own purchases. With
              employees in the owner's copy it is the business's money and
              the business's supplier balances, so it is the manager's. A
              driver who pays cash on the road records that on the purchase
              itself, which is still his to do. */}
          <Route path="pay-supplier" element={<ManagerRoute><PaySupplier /></ManagerRoute>} />
          <Route path="warehouse" element={<ManagerRoute><FieldWarehouse /></ManagerRoute>} />
          <Route path="staff" element={<ManagerRoute><FieldUsers /></ManagerRoute>} />
          <Route path="settings" element={<ManagerRoute><FieldSettings /></ManagerRoute>} />
          <Route path="doc-setup" element={<ManagerRoute><FieldDocSetup /></ManagerRoute>} />
          <Route path="price-check" element={<ManagerRoute><FieldPriceCheck /></ManagerRoute>} />
        </Route>

        {/* Owner mode. Guarded by the same auth as everything else; the
            REAL gate is server-side (ownerMiddleware 403s an account with
            no grant), so reaching this URL without one shows an error
            rather than anyone else's numbers. */}
        <Route
          path="/owner"
          element={
            <ProtectedRoute>
              <OwnerMonitor />
            </ProtectedRoute>
          }
        />
        {/* Before the :tenantId route, or "add-van" would be read as a
            tenant id and 403 against the grant. */}
        <Route
          path="/owner/add-van"
          element={
            <ProtectedRoute>
              <OwnerAddVan />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/add-operator"
          element={
            <ProtectedRoute>
              <OwnerAddOperator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/owner/:tenantId"
          element={
            <ProtectedRoute>
              <OwnerOperator />
            </ProtectedRoute>
          }
        />

        {/* Office Mode retired — everything lives in field mode now.
            Old bookmarks / installed PWAs pointing at /office/* land on
            field Home rather than a 404. */}
        <Route path="/office/*" element={<Navigate to="/field" replace />} />

        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/field" replace />} />
      </Routes>
    </Suspense>
  )
}
